import React, { useEffect, useMemo, useState } from "react";
import logo from "../../Assets/logo.png";
import { Link, useLocation } from "react-router-dom";
import { useSidebar } from "../ContextAPI/SidebarContext";
import CryptoJS from "crypto-js";
import { API_IMAGE_URL, CRYPTO_TOKEN_SECRET_KEY } from "../../config";
import { getPermissionData } from "../../Actions/common";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import CopyrightRoundedIcon from "@mui/icons-material/CopyrightRounded";
import Tooltip from "@mui/material/Tooltip";
import {
	MyAccessTimeIcon,
	MyArticleOutlinedIcon,
	MyAssessmentIcon,
	MyDashboardCustomizeOutlinedIcon,
	MyDescriptionIcon,
	MyPaymentIcon,
	MyPeopleOutlineOutlinedIcon,
	MySupportAgentIcon,
	MyWorkspacePremiumIcon,
} from "../../Assets/svg/MyIcons";
import { useUser } from "../ContextAPI/ProfileContext";
import "./Sidebar.css";

const COPYRIGHT_TEXT = `© ${new Date().getFullYear()} Netclues Master. All Rights Reserved.`;

const ICON_SIZE = 22;

const getUserData = () => {
	const decryptedData = CryptoJS.AES.decrypt(
		localStorage.getItem("userData"),
		CRYPTO_TOKEN_SECRET_KEY
	).toString(CryptoJS.enc.Utf8);
	return JSON.parse(decryptedData);
};

const isPathActive = (pathname, to) => {
	if (!to) return false;
	return pathname === to || pathname.startsWith(`${to}/`);
};

const getInitials = (user) => {
	const first = user?.firstName?.[0] || "";
	const last = user?.lastName?.[0] || "";
	const initials = `${first}${last}`.toUpperCase();
	return initials || "U";
};

const SideBarComponent = ({ isSandbox, permission: permissionProp }) => {
	const { sideBarOpen } = useSidebar();
	const location = useLocation();
	const activeMenu = location.pathname;
	const { userDetails } = useUser();

	const userData = localStorage.getItem("userData") ? getUserData() : null;
	const permission =
		Array.isArray(permissionProp) && permissionProp.length > 0
			? permissionProp
			: localStorage.getItem(`${userData?.id}_permissionData`)
				? getPermissionData(`${userData?.id}_permissionData`)
				: [];
	const profileUser = userDetails?.data || userDetails || userData;
	const profileName = [profileUser?.firstName, profileUser?.lastName]
		.filter(Boolean)
		.join(" ")
		.trim();
	const profileRole =
		permission?.[0]?.roleName ||
		profileUser?.role?.roleName ||
		profileUser?.email ||
		"Profile";
	const profilePic = profileUser?.profilePic
		? `${API_IMAGE_URL}/qbotmasterapp.netcluesdemo.com/profilePics/${profileUser.profilePic}`
		: "";
	const profileActive = isPathActive(activeMenu, "/profile");
	const [avatarFailed, setAvatarFailed] = useState(false);

	useEffect(() => {
		setAvatarFailed(false);
	}, [profilePic]);

	const menuGroups = useMemo(
		() => [
			{
				label: "Main",
				items: [
					{
						slug: "dashboard",
						title: "Dashboard",
						icon: (
							<MyDashboardCustomizeOutlinedIcon
								width={ICON_SIZE}
								height={ICON_SIZE}
							/>
						),
						to: "/dashboard",
						children: [
							{
								slug: "dashboard",
								title: "Overview",
								to: "/dashboard",
							},
						],
						alwaysVisible: true,
					},
					{
						slug: "onboarding-tenant",
						title: "Tenant User",
						icon: (
							<MyPeopleOutlineOutlinedIcon
								width={ICON_SIZE}
								height={ICON_SIZE}
							/>
						),
						to: "/tenant",
						children: [
							{
								slug: "onboarding-tenant",
								title: "Tenant User",
								to: "/tenant",
							},
						],
					},
					{
						slug: "transaction",
						title: "Transactions",
						icon: (
							<MyPaymentIcon width={ICON_SIZE} height={ICON_SIZE} />
						),
						to: "/transactions",
						children: [
							{
								slug: "transaction",
								title: "Transactions",
								to: "/transactions",
							},
						],
					},
					{
						slug: "reports",
						title: "Reports",
						icon: (
							<MyAssessmentIcon
								width={ICON_SIZE}
								height={ICON_SIZE}
							/>
						),
						to: "/reports",
						children: [
							{
								slug: "reports",
								title: "Reports",
								to: "/reports",
							},
						],
					},
				],
			},
			{
				label: "Management",
				items: [
					{
						slug: "plan-management",
						title: "Plan Management",
						icon: (
							<MyWorkspacePremiumIcon
								width={ICON_SIZE}
								height={ICON_SIZE}
							/>
						),
						to: "/plan-management",
						children: [
							{
								slug: "plan-management",
								title: "Plan Management",
								to: "/plan-management",
							},
							{
								slug: "plan-features",
								title: "Plan Features",
								to: "/plan-feature-management",
							},
						],
					},
					{
						slug: "kyc-country-document",
						title: "KYC Country Document",
						icon: (
							<MyDescriptionIcon
								width={ICON_SIZE}
								height={ICON_SIZE}
							/>
						),
						to: "/country-document",
						children: [
							{
								slug: "kyc-country-document",
								title: "KYC Country Document",
								to: "/country-document",
							},
							{
								slug: "kyc-document-type",
								title: "KYC Country Document Type",
								to: "/documentation-type",
							},
						],
					},
					{
						slug: "paycentral-invoice",
						title: "Automated Invoice",
						icon: (
							<MyArticleOutlinedIcon
								width={ICON_SIZE}
								height={ICON_SIZE}
							/>
						),
						to: "/invoices",
						children: [
							{
								slug: "paycentral-invoice",
								title: "Automated Invoice",
								to: "/invoices",
							},
						],
					},
					{
						slug: "knowledge-base",
						title: "Knowledge Base",
						icon: (
							<MyDescriptionIcon
								width={ICON_SIZE}
								height={ICON_SIZE}
							/>
						),
						to: "/knowledge-base",
						children: [
							{
								slug: "knowledge-base",
								title: "Knowledge Base",
								to: "/knowledge-base",
							},
						],
					},
					{
						slug: "tenant-sla",
						title: "Tenant SLA Management",
						icon: (
							<MyAccessTimeIcon
								width={ICON_SIZE}
								height={ICON_SIZE}
							/>
						),
						to: "/tenant-sla",
						children: [
							{
								slug: "tenant-sla",
								title: "Tenant SLA Management",
								to: "/tenant-sla",
							},
						],
					},
				],
			},
			{
				label: "Administration",
				items: [
					{
						slug: "users",
						title: "User Management",
						icon: (
							<MyPeopleOutlineOutlinedIcon
								width={ICON_SIZE}
								height={ICON_SIZE}
							/>
						),
						to: "/users",
						children: [
							{
								slug: "users",
								title: "Users",
								to: "/users",
							},
							{
								slug: "roles",
								title: "Role Management",
								to: "/role-manager",
							},
						],
					},
					{
						slug: "audit-trail",
						title: "Audit Trail",
						icon: (
							<MyArticleOutlinedIcon
								width={ICON_SIZE}
								height={ICON_SIZE}
							/>
						),
						to: "/audit-trail",
						children: [
							{
								slug: "audit-trail",
								title: "Audit Trail",
								to: "/audit-trail",
							},
							{
								slug: "mail-logs",
								title: "Email Logs",
								to: "/mail-logs",
							},
							{
								slug: "master-tenant-logs",
								title: "Master Tenant Logs",
								to: "/master-tenant-logs",
							},
						],
					},
					{
						slug: "support-ticket",
						title: "Ticketing System",
						icon: (
							<MySupportAgentIcon
								width={ICON_SIZE}
								height={ICON_SIZE}
							/>
						),
						to: "/support-ticket",
						children: [
							{
								slug: "support-ticket",
								title: "Ticketing System",
								to: "/support-ticket",
							},
						],
					},
				],
			},
		],
		[]
	);

	const canView = (slug) => {
		const module = permission[0]?.role_permissions?.find(
			(m) => m?.module?.slug === slug
		);
		return Boolean(module?.canView);
	};

	const getVisibleChildren = (menuItem) =>
		(menuItem.children || []).filter((child) => canView(child.slug));

	const getActiveParentSlug = (pathname) => {
		for (const group of menuGroups) {
			for (const item of group.items) {
				if (item.alwaysVisible || canView(item.slug)) {
					const children = item.alwaysVisible
						? item.children
						: getVisibleChildren(item);
					if (
						children?.some((child) =>
							isPathActive(pathname, child.to)
						) ||
						isPathActive(pathname, item.to)
					) {
						return item.slug;
					}
				}
			}
		}
		return "dashboard";
	};

	const [openMenus, setOpenMenus] = useState({ dashboard: true });

	useEffect(() => {
		const active = getActiveParentSlug(activeMenu);
		if (active) {
			setOpenMenus((prev) => ({ ...prev, [active]: true }));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeMenu]);
	const toggleMenu = (slug, event) => {
		event?.preventDefault();
		event?.stopPropagation();
		setOpenMenus((prev) => ({
			...prev,
			[slug]: !prev[slug],
		}));
	};

	const renderMenuItem = (menuItem) => {
		const children = menuItem.alwaysVisible
			? menuItem.children
			: getVisibleChildren(menuItem);

		if (!menuItem.alwaysVisible && !canView(menuItem.slug)) return null;
		if (!menuItem.alwaysVisible && children.length === 0) return null;

		const hasChildren = children && children.length > 0;
		const childSelected = children.some((child) =>
			isPathActive(activeMenu, child.to)
		);
		const parentActive =
			isPathActive(activeMenu, menuItem.to) || childSelected;
		const isOpen = Boolean(openMenus[menuItem.slug]);

		// Single-child menus navigate directly (cleaner for items like Reports)
		const isSingleLink =
			hasChildren &&
			children.length === 1 &&
			children[0].to === menuItem.to;

		if (!hasChildren || isSingleLink) {
			const linkTo = children?.[0]?.to || menuItem.to;
			return (
				<li key={menuItem.slug} className="mp-nav-item">
					<Link
						to={linkTo}
						className={`menu-item ${parentActive ? "active" : ""}`}
						title={menuItem.title}
					>
						<span className="menu-icon">{menuItem.icon}</span>
						<span className="menu-title">{menuItem.title}</span>
					</Link>
				</li>
			);
		}

		return (
			<li
				key={menuItem.slug}
				className={`mp-nav-item sb-menu ${isOpen ? "is-open" : ""}`}
			>
				<button
					type="button"
					className={`menu-item ${parentActive ? "active" : ""}`}
					title={menuItem.title}
					onClick={(e) => toggleMenu(menuItem.slug, e)}
					aria-expanded={isOpen}
				>
					<span className="menu-icon">{menuItem.icon}</span>
					<span className="menu-title">{menuItem.title}</span>
					<span className="mp-chevron">
						<KeyboardArrowDownRoundedIcon />
					</span>
				</button>

				<ul className="mp-submenu list-unstyled">
					<li className="mp-submenu-title">{menuItem.title}</li>
					{children.map((child) => (
						<li key={child.slug}>
							<Link
								to={child.to}
								className={`menu-item ${isPathActive(activeMenu, child.to)
										? "active"
										: ""
									}`}
							>
								<span className="menu-title">{child.title}</span>
							</Link>
						</li>
					))}
				</ul>
			</li>
		);
	};

	const drawerClass = sideBarOpen
		? "aside drawer drawer-start drawer-on"
		: "aside drawer drawer-start";

	return (
		<div
			id="kt_aside"
			className={`mp-sidebar ${drawerClass} ${isSandbox ? "sandbox" : ""
				}`}
			data-kt-drawer="true"
			data-kt-drawer-name="aside"
			data-kt-drawer-activate="{default: true, lg: false}"
			data-kt-drawer-overlay="true"
			data-kt-drawer-width="{default:'80px', '300px': '100px'}"
			data-kt-drawer-direction="start"
			data-kt-drawer-toggle="#kt_aside_mobile_toggle"
		>
			<div className="aside-menu flex-column-fluid" id="kt_aside_menu">
				{/* <div className="mp-sidebar-brand">
					<Link to="/dashboard">
						<img alt="Logo" src={logo} className="mp-logo-full" />
					</Link>
				</div> */}

				<nav className="mp-sidebar-nav" aria-label="Main navigation">
					<div
						className="menu menu-column menu-title-gray-700 h-100"
						id="#kt_aside_menu"
						data-kt-menu="true"
					>
						{menuGroups.map((group, groupIndex) => {
							const items = group.items
								.map((item) => renderMenuItem(item))
								.filter(Boolean);

							if (items.length === 0) return null;

							const isLastGroup =
								groupIndex === menuGroups.length - 1;

							return (
								<ul
									key={`group-${group.label}`}
									className={`mp-nav-list list-unstyled ${groupIndex > 0 ? "mp-nav-divider" : ""
										} ${isLastGroup ? "mp-nav-spacer" : ""}`}
								>
									<li className="mp-section-label" aria-hidden="true">
										{group.label}
									</li>
									{items}
								</ul>
							);
						})}
					</div>
				</nav>

				<div className="mp-sidebar-profile">
					<Link
						to="/profile"
						className={`mp-profile-link ${profileActive ? "is-active" : ""
							}`}
						aria-label="Open profile"
						title={profileName || "Profile"}
					>
						<span className="mp-profile-avatar" aria-hidden>
							{profilePic && !avatarFailed ? (
								<img
									src={profilePic}
									alt=""
									onError={() => setAvatarFailed(true)}
								/>
							) : (
								getInitials(profileUser)
							)}
						</span>
						<span className="mp-profile-meta">
							<p className="mp-profile-name">
								{profileName || "Your profile"}
							</p>
							<p className="mp-profile-role">{profileRole}</p>
						</span>
					</Link>
					<Tooltip
						title={COPYRIGHT_TEXT}
						placement="right"
						arrow
						enterDelay={200}
					>
						<button
							type="button"
							className="mp-copyright-btn"
							aria-label={COPYRIGHT_TEXT}
						>
							<CopyrightRoundedIcon fontSize="inherit" />
						</button>
					</Tooltip>
				</div>
			</div>
		</div>
	);
};

export default SideBarComponent;
